import React, { useRef, useMemo } from 'react';

import Filter from '@/pages/dashboard/components/NewFilter';
import { formatRetentionDate, setBtnPointLog } from "@/pages/dashboard/utils/utils";
import { useSelector } from 'umi';
import { commonDateDefault } from '../../utils/dateUtils';

export interface IButtonLogConfig {
  confirmButtonId?: string;
  resetButtonId?: string;
  pageId?: string;
  typeId?: string;
}

export interface ICasualLtvFilterProps {
  /** 筛选器确认函数 */
  filterConfirm?: (value: Record<string, any>) => void;
  /** 筛选器重置函数 */
  filterReset?: (value: Record<string, any>) => void;
  /** 筛选器 loading 设置，获取到时区等数据后设置为 false */
  setFilterLoading?: (loading: boolean) => void;
  /** 初始化函数，向外部传递初始化后的筛选器值 */
  filterInit?: (value: Record<string, any>) => void;
  /** 打点配置 */
  buttonLogConfig?: IButtonLogConfig;
  /** 是否开启吸顶 */
  openSticky?: boolean;
  /** 筛选器路由路径 */
  path?: string;
  children?: React.ReactNode;
  /** 是否有筛选器保存功能 */
  isHaveFilterSave?: boolean;
  /** 时区偏移，默认 0 */
  timeZone?: number;
}

const firstWordUpper = (str: string) => {
  if (!str) return str;
  const firstLetter = str.charAt(0);
  const firstLetterCap = firstLetter.toUpperCase();
  const remainingLetters = str.slice(1);
  return firstLetterCap + remainingLetters === 'Date' ? 'Day' : firstLetterCap + remainingLetters;
};

const CasualLtvFilter = (props: ICasualLtvFilterProps) => {
  const { filterConfirm, filterReset, buttonLogConfig, setFilterLoading, filterInit, openSticky = false, path, children, isHaveFilterSave, timeZone = 0 } = props;
  // daily筛选器继承逻辑
  const filterValueRef = useRef<any>(null); // 筛选器值，此处不用state，更新formValue同样会触发组件更新

  const seasonInfo = useSelector((state: any) => 
    // 初始可能为undefied,在此加兜底
    state?.dashboardSetting?.gameConfig?.season_info || { date: [], week: [], month: [] }
  );

  const inputList = useMemo(() => [commonDateDefault(timeZone, seasonInfo)], [timeZone, seasonInfo]);
  const handleConfirm = (value: any) => {
    filterValueRef.current = value;
    const formatRangeTime = formatRetentionDate(value?.rangeTime?.value, value?.rangeTime?.type);
    filterConfirm?.({
      ...value,
      rangeTime: [
        formatRangeTime?.[0]?.format('YYYY-MM-DD'),
        formatRangeTime?.[1]?.format('YYYY-MM-DD')
      ],
      timeType: firstWordUpper(value?.rangeTime?.type),
    });
    // 打点，沟通后打点操作放在业务代码的confirm和reset中，不封装在新版本筛选器中
    setBtnPointLog(buttonLogConfig, value);
  };

  const handleReset = (value: any) => {
    filterValueRef.current = value;
    const formatRangeTime = formatRetentionDate(value?.rangeTime?.value, value?.rangeTime?.type);
    filterReset?.({
      ...value,
      rangeTime: [
        formatRangeTime?.[0]?.format('YYYY-MM-DD'),
        formatRangeTime?.[1]?.format('YYYY-MM-DD')
      ],
      timeType: firstWordUpper(value?.rangeTime?.type),
    });
    // 打点，沟通后打点操作放在业务代码的confirm和reset中，不封装在新版本筛选器中
    setBtnPointLog(buttonLogConfig, value, true);
  };

  const handleInit = (value: any) => {
    filterValueRef.current = value;
    const formatRangeTime = formatRetentionDate(value?.rangeTime?.value, value?.rangeTime?.type);
    filterInit?.({
      ...value,
      rangeTime: [
        formatRangeTime?.[0]?.format('YYYY-MM-DD'),
        formatRangeTime?.[1]?.format('YYYY-MM-DD')
      ],
      timeType: firstWordUpper(value?.rangeTime?.type),
    });
    setFilterLoading?.(false);
  };

  return (
    <Filter
      openSticky={openSticky}
      path={path}
      value={filterValueRef.current}
      latestDateType={''}
      inputList={inputList}
      onInit={handleInit}
      onConfirm={handleConfirm}
      onReset={handleReset}
      isHaveFilterSave={isHaveFilterSave}
    >
      {children}
    </Filter>
  );
};

export default CasualLtvFilter;
